import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import {
  useOnboardingStages,
  useOnboardingStageById,
  useDeleteOnboardingStage,
  useUpdateOnboardingWorkflowsOrder
} from "@/pages/settings/components/contractsSettings/hooks";
import {
  OnboardingStage,
  OnboardingStageWorkflow
} from "@/pages/settings/components/contractsSettings/types";
import Section from "@/pages/settings/components/sections/Section";
import { Button } from "@/components/ui/button";
import { SettingItemLayoutProps } from "@/pages/settings/components/settingsItems/SettingItemLayout";
import Text from "@/components/ui/text";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import { AlertBubbleWarning } from "@dreamhub/designsystem/icons";
import SettingsNavigation from "@/pages/settings/components/SettingsNavigation";
import Skeleton from "@/components/ui/skeleton";
import AddActionButton from "@/components/ui/add-action-button";
import EmptyQuestionnaires from "@/pages/settings/components/dealsSettings/StageStudio/EmptyQuestionnaires";
import DraggableWorkflowListContainer from "@/pages/settings/components/dealsSettings/StageStudio/workflow/DraggableWorkflowListContainer";
import AddWorkflowModal from "./addWorkflow/AddWorkflowModal";
import DeleteOnboardingStageModal from "./components/DeleteOnboardingStageModal";
import EditOnboardingStageDrawer from "./components/EditOnboardingStageDrawer";
import { getOnboardingStageColor } from "./colors";
import toast from "react-hot-toast";
import { isPublishedOnboardingItem } from "./workflow/helpers";

const TRANSLATION_PREFIX = "settings.contracts.onboardingStudio.stageDetail";
const DELETE_MODAL_PREFIX =
  "settings.contracts.onboardingStudio.deleteStageModal";
const STUDIO_PATH = "/settings/contracts?tab=onboardingStudio";

const OnboardingStageDetailedView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { stageId } = useParams<{ stageId: string }>();

  const { data: stage, isLoading } = useOnboardingStageById(
    stageId || "",
    !isEmpty(stageId)
  );

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ["settings/contracts/stages/stageById"]
    });
  }, [queryClient]);

  const { data: stagesData } = useOnboardingStages();
  const stages = stagesData?.stages || [];

  const deleteStage = useDeleteOnboardingStage();
  const updateWorkflowsOrder = useUpdateOnboardingWorkflowsOrder();

  const [isDeleteStageModalOpen, setIsDeleteStageModalOpen] = useState(false);
  const [isEditStageDrawerOpen, setIsEditStageDrawerOpen] = useState(false);
  const [isAddWorkflowModalOpen, setIsAddWorkflowModalOpen] = useState(false);
  const [localWorkflows, setLocalWorkflows] = useState<
    OnboardingStageWorkflow[]
  >([]);

  const workflows = useMemo(() => stage?.workflows || [], [stage?.workflows]);

  useEffect(() => {
    setLocalWorkflows([...workflows]);
  }, [workflows]);

  const handleWorkflowsReorder = async (orderedIds: string[]) => {
    const reordered = orderedIds
      .map((id) => workflows.find((w) => w.id === id))
      .filter((wf): wf is OnboardingStageWorkflow => wf !== undefined);
    setLocalWorkflows(reordered);
    try {
      await updateWorkflowsOrder.mutateAsync({ data: orderedIds });
      queryClient.invalidateQueries({
        queryKey: ["settings/contracts/stages/stageById"]
      });
    } catch {
      setLocalWorkflows([...workflows]);
    }
  };

  const handleAddWorkflow = () => setIsAddWorkflowModalOpen(true);

  const otherStages = useMemo(
    () => stages.filter((s) => s.id !== stageId),
    [stages, stageId]
  );

  const handleDeleteStage = (targetStageId: string | null) => {
    deleteStage.mutate(
      {
        pathParams: { stageId: stageId || "" },
        ...(targetStageId !== null ? { data: { targetStageId } } : {})
      },
      {
        onSuccess: () => {
          setIsDeleteStageModalOpen(false);
          queryClient.invalidateQueries({
            queryKey: ["settings/contracts/stages"]
          });
          navigate(STUDIO_PATH);
          toast.success(
            t(`${DELETE_MODAL_PREFIX}.successToast`, {
              stageName: stage?.name
            }),
            { duration: 5000 }
          );
        },
        onError: () => {
          toast.error(t(`${DELETE_MODAL_PREFIX}.errorToast`));
        }
      }
    );
  };

  return (
    <>
      <div className="flex w-full items-center justify-center">
        <div className="w-full max-w-[800px] flex flex-col gap-12 pt-6 pb-12">
          <div className="flex flex-col gap-10">
            <SettingsNavigation
              isLoading={isLoading}
              items={[
                {
                  label: t(
                    `${TRANSLATION_PREFIX}.navigation.contractsSettings`
                  ),
                  to: "/settings/contracts"
                },
                {
                  label: t(`${TRANSLATION_PREFIX}.navigation.onboardingStudio`),
                  to: STUDIO_PATH
                },
                {
                  label: t(`${TRANSLATION_PREFIX}.navigation.stageName`, {
                    stage: stage?.name
                  }),
                  isActive: true
                }
              ]}
            />
            {isLoading ? (
              <Skeleton className="h-7 w-[15rem]" />
            ) : (
              <div className="flex gap-4 items-center">
                <div
                  className="block w-2 rounded-[28px] h-[52px]"
                  style={{
                    backgroundColor: getOnboardingStageColor(stage?.stageColor)
                  }}
                />
                <div className="flex flex-col">
                  <Text variant="title3H1">{stage?.name}</Text>
                  <Text variant="bodySecondary" className="text-secondary">
                    {t(`${TRANSLATION_PREFIX}.stageLabel`)}
                  </Text>
                </div>
              </div>
            )}
          </div>
          <Section
            isLoading={isLoading}
            title={t(`${TRANSLATION_PREFIX}.details`)}
            action={
              <Button
                variant="tertiary"
                onClick={() => setIsEditStageDrawerOpen(true)}
                disabled={isLoading}
              >
                {t("edit")}
              </Button>
            }
            items={
              [
                {
                  title: t(`${TRANSLATION_PREFIX}.name`),
                  textValue: stage?.name,
                  className: "p-4",
                  titleClassName: "text-secondary"
                },
                {
                  title: t(`${TRANSLATION_PREFIX}.description`),
                  textValue: stage?.description || "",
                  className: "p-4",
                  titleClassName: "text-secondary"
                }
              ] as SettingItemLayoutProps[]
            }
          />
          <Section
            isLoading={isLoading}
            title={t(`${TRANSLATION_PREFIX}.workflows`)}
            subTitle={t(`${TRANSLATION_PREFIX}.workflowsSubtitle`)}
            action={
              workflows.length > 0 ? (
                <AddActionButton
                  label={t(`${TRANSLATION_PREFIX}.addWorkflow`)}
                  onClick={handleAddWorkflow}
                />
              ) : undefined
            }
          >
            {isEmpty(localWorkflows) ? (
              <EmptyQuestionnaires
                tPrefix={TRANSLATION_PREFIX}
                onButtonClick={handleAddWorkflow}
              />
            ) : (
              <DraggableWorkflowListContainer
                items={localWorkflows.map((workflow) => ({
                  id: workflow.id,
                  name: workflow.name,
                  isGuardrails: workflow.isGuardrails,
                  numQuestions: (workflow.items ?? []).filter(
                    isPublishedOnboardingItem
                  ).length
                }))}
                onReorder={handleWorkflowsReorder}
              />
            )}
          </Section>
          {isLoading ? null : (
            <Section
              items={
                [
                  {
                    title: t(`${TRANSLATION_PREFIX}.dangerZone`),
                    subTitle: t(`${TRANSLATION_PREFIX}.dangerZoneSubtitle`),
                    icon: (
                      <LogoIconContainer
                        className="size-10"
                        icon={<AlertBubbleWarning />}
                        variant="danger"
                      />
                    ),
                    actionType: "button",
                    actionProps: {
                      variant: "tertiary",
                      destructive: true,
                      onClick: () => setIsDeleteStageModalOpen(true),
                      label: t(`${TRANSLATION_PREFIX}.deleteStage`)
                    }
                  }
                ] as SettingItemLayoutProps[]
              }
            />
          )}
        </div>
      </div>
      <DeleteOnboardingStageModal
        isOpen={isDeleteStageModalOpen}
        setIsOpen={setIsDeleteStageModalOpen}
        handleDelete={handleDeleteStage}
        stage={(stage as OnboardingStage) || null}
        otherStages={otherStages}
        isPending={deleteStage.isPending}
      />
      <EditOnboardingStageDrawer
        isOpen={isEditStageDrawerOpen}
        onClose={(stageWasUpdated) => {
          setIsEditStageDrawerOpen(false);
          if (stageWasUpdated) {
            queryClient.invalidateQueries({
              queryKey: ["settings/contracts/stages/stageById"]
            });
          }
        }}
        stageData={(stage as OnboardingStage) || null}
        allStages={stages}
      />
      <AddWorkflowModal
        isOpen={isAddWorkflowModalOpen}
        setIsOpen={setIsAddWorkflowModalOpen}
        stageId={stageId}
      />
    </>
  );
};

export default OnboardingStageDetailedView;
